import { AlertTriangle, CheckCircle2, Clock, Plus, ShieldCheck, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import {
  COVENANT_STATUS_CONFIG,
  COVENANT_TYPE_LABELS,
  type CovenantType,
  useCheckCovenant,
  useCovenantSummary,
  useCovenants,
  useCreateCovenant,
} from '@/hooks/use-covenants';

function fmtDate(d?: string): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

interface Props {
  loanId: string;
}

export function CovenantTab({ loanId }: Props) {
  const { data: covenants, isLoading } = useCovenants(loanId);
  const { data: summary } = useCovenantSummary(loanId);
  const createCov = useCreateCovenant(loanId);
  const checkCov = useCheckCovenant(loanId);
  const [showCreate, setShowCreate] = useState(false);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<'PASS' | 'FAIL' | 'WAIVED'>('PASS');
  const [checkNotes, setCheckNotes] = useState('');

  const [form, setForm] = useState({
    type: 'LTV_RATIO' as CovenantType,
    name: '',
    description: '',
    threshold: '',
    frequency: 'MONTHLY',
    nextDue: new Date().toISOString().split('T')[0],
  });

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const input: any = { ...form };
    if (input.nextDue) input.nextDue = new Date(input.nextDue).toISOString();
    if (!input.threshold) delete input.threshold;
    createCov.mutate(input, {
      onSuccess: () => {
        setShowCreate(false);
        setForm({ ...form, name: '', description: '', threshold: '' });
      },
    });
  }

  function handleCheck(covenantId: string) {
    checkCov.mutate(
      { covenantId, result: checkResult, notes: checkNotes },
      {
        onSuccess: () => {
          setCheckingId(null);
          setCheckNotes('');
          setCheckResult('PASS');
        },
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Total</p>
            <p className="text-xl font-bold text-gray-900">{summary.total}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Active</p>
            <p className="text-xl font-bold text-blue-600">{summary.active}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Breached</p>
            <p
              className={`text-xl font-bold ${summary.breached > 0 ? 'text-red-600' : 'text-gray-900'}`}
            >
              {summary.breached}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Compliance</p>
            <p
              className={`text-xl font-bold ${summary.complianceRate >= 90 ? 'text-green-600' : summary.complianceRate >= 70 ? 'text-amber-600' : 'text-red-600'}`}
            >
              {summary.complianceRate.toFixed(0)}%
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-semibold text-gray-900">Covenants & Conditions</h3>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowCreate(!showCreate)}
          className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-white text-sm rounded-md hover:"
        >
          <Plus className="w-3.5 h-3.5" /> Add Covenant
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Type</label>
              <select
                value={form.type}
                onChange={e => setForm({ ...form, type: e.target.value as CovenantType })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                {Object.entries(COVENANT_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Name *</label>
              <input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                required
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                placeholder="e.g. LTV below 80%"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Threshold</label>
              <input
                value={form.threshold}
                onChange={e => setForm({ ...form, threshold: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                placeholder="e.g. 80%"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Frequency</label>
              <select
                value={form.frequency}
                onChange={e => setForm({ ...form, frequency: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                <option value="MONTHLY">Monthly</option>
                <option value="QUARTERLY">Quarterly</option>
                <option value="ANNUALLY">Annually</option>
                <option value="ONE_OFF">One-off</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Description</label>
            <textarea
              rows={2}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm resize-none"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              type="submit"
              disabled={createCov.isPending}
              className="w-fit px-3 py-1.5 text-white text-sm rounded hover: disabled:opacity-50"
            >
              Create
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowCreate(false)}
              className="w-fit"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Covenant list */}
      {covenants && covenants.length > 0 ? (
        <div className="space-y-2">
          {covenants.map(cov => {
            const statusCfg = COVENANT_STATUS_CONFIG[cov.status];
            const isOverdue =
              cov.nextDue && new Date(cov.nextDue) < new Date() && cov.status === 'ACTIVE';
            return (
              <div
                key={cov.id}
                className={`bg-white border rounded-lg p-3 ${isOverdue ? 'border-red-200' : 'border-gray-200'}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {cov.status === 'BREACHED' ? (
                      <XCircle className="w-4 h-4 text-red-500" />
                    ) : cov.status === 'MET' ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <ShieldCheck className="w-4 h-4 text-blue-500" />
                    )}
                    <span className="text-sm font-medium text-gray-900">{cov.name}</span>
                    <span
                      className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: statusCfg.bg, color: statusCfg.color }}
                    >
                      {statusCfg.label}
                    </span>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                      {COVENANT_TYPE_LABELS[cov.type]}
                    </span>
                  </div>
                  {cov.status !== 'EXPIRED' && (
                    <button
                      onClick={() => setCheckingId(checkingId === cov.id ? null : cov.id)}
                      className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      Record Check
                    </button>
                  )}
                </div>

                {cov.description && <p className="text-xs text-gray-500 mb-1">{cov.description}</p>}

                <div className="flex items-center gap-4 text-[10px] text-gray-400">
                  {cov.threshold && <span>Threshold: {cov.threshold}</span>}
                  <span>Frequency: {cov.frequency}</span>
                  {cov.lastChecked && <span>Last checked: {fmtDate(cov.lastChecked)}</span>}
                  {cov.nextDue && (
                    <span
                      className={
                        isOverdue ? 'text-red-500 font-medium flex items-center gap-1' : ''
                      }
                    >
                      {isOverdue && <AlertTriangle className="w-3 h-3" />}
                      Next due: {fmtDate(cov.nextDue)}
                    </span>
                  )}
                </div>

                {/* Check form */}
                {checkingId === cov.id && (
                  <div className="mt-3 flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <select
                      value={checkResult}
                      onChange={e => setCheckResult(e.target.value as any)}
                      className="px-2 py-1 border border-gray-300 rounded text-xs"
                    >
                      <option value="PASS">Pass</option>
                      <option value="FAIL">Fail</option>
                      <option value="WAIVED">Waived</option>
                    </select>
                    <input
                      value={checkNotes}
                      onChange={e => setCheckNotes(e.target.value)}
                      placeholder="Notes..."
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                    />
                    <Button
                      variant="primary"
                      onClick={() => handleCheck(cov.id)}
                      disabled={checkCov.isPending}
                      className="w-fit px-2 py-1 min-h-0 text-white text-xs rounded hover: disabled:opacity-50"
                    >
                      Save
                    </Button>
                  </div>
                )}

                {/* Recent checks */}
                {cov.checks && cov.checks.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {cov.checks.slice(0, 3).map(check => (
                      <div
                        key={check.id}
                        className="flex items-center gap-2 text-[10px] text-gray-400"
                      >
                        <Clock className="w-3 h-3" />
                        <span>{fmtDate(check.checkDate)}</span>
                        <span
                          className={`font-medium ${check.result === 'PASS' ? 'text-green-600' : check.result === 'FAIL' ? 'text-red-600' : 'text-amber-600'}`}
                        >
                          {check.result}
                        </span>
                        {check.notes && <span className="text-gray-400">— {check.notes}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-10 text-gray-400">
          <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No covenants set for this loan</p>
        </div>
      )}
    </div>
  );
}
